"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, CircleCheck as CheckCircle2, PhoneCall, Sparkles, MessageSquare } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqs = [{
  question: "How long does it take to see local SEO results in Chelmsford?",
  answer: "Typically, local SEO improvements in Chelmsford and Essex begin showing within 3 to 6 months. Initial technical fixes and Google Business Profile optimizations often trigger early movement within weeks, while lasting top-map-pack placements build up progressively as local authority grows."
}, {
  question: "Why should I work directly with an independent SEO consultant rather than a Chelmsford agency?",
  answer: "When you hire me, you get direct access to my hands-on expertise. There are no account managers, junior staff learning on your budget, or inflated agency overheads. Every audit, content strategy, and link building action is executed personally by me with full accountability."
}, {
  question: "What specific tasks are included in your Chelmsford Local SEO services?",
  answer: "My local SEO package covers comprehensive Google Business Profile management, local schema markup, hyper-local Essex content creation, local citation cleaning, technical health audits, review generation strategy, and acquiring relevant backlinks from Essex and UK websites."
}, {
  question: "Do I still need local SEO if I already run Google Ads in Essex?",
  answer: "Yes. Paid ads stop delivering leads the moment your ad budget runs out. Local SEO builds a permanent, compounding organic presence that captures high-intent customers who intentionally skip ads to find trusted local businesses in Chelmsford."
}, {
  question: "How do you optimize my Google Business Profile for Chelmsford searchers?",
  answer: "I systematically refine your primary and secondary business categories, write search-optimized service descriptions, implement local geo-coordinates context, upload optimized photos, set up Q&As, and structure a steady review strategy tailored to Chelmsford buyers."
}, {
  question: "Can you help my business rank in neighboring Essex areas like Braintree and Maldon?",
  answer: "Absolutely. We start by establishing strong dominance in Chelmsford, then expand your footprint across surrounding Essex towns (such as Braintree, Maldon, Brentwood, and Colchester) through dedicated, high-quality location pages and localized link strategies."
}, {
  question: "How do you measure and report local SEO success?",
  answer: "I provide transparent, jargon-free monthly reports. We track real business growth metrics: local map pack rankings, organic search visits, phone calls generated directly from Google Maps, form inquiries, and revenue impact rather than vanity figures."
}, {
  question: "What makes local SEO in Chelmsford different from national SEO?",
  answer: "Chelmsford local SEO focuses heavily on geo-targeted intent, local proximity signals, map pack visibility, and local community citations. National SEO targets broader keywords without local intent, requiring a completely different architectural and backlink strategy."
}, {
  question: "Will I need to redesign or rebuild my current website?",
  answer: "Rarely. In most cases, I optimize your existing website on platforms like WordPress, Shopify, Webflow, or custom stacks. I will recommend technical modifications only if your current foundation actively prevents search engines from indexing your pages."
}, {
  question: "How much does local SEO consultancy in Chelmsford cost?",
  answer: "Because every business faces different competitive dynamics, my services are tailored rather than packaged into rigid tiers. Book a free consultation call with me and I will provide an honest, fixed monthly quote based on your exact growth targets."
}, {
  question: "Do you require long-term lock-in contracts?",
  answer: "No. I work on flexible monthly retainers because I believe in earning your partnership continuously through measurable value, transparent communication, and solid organic growth."
}, {
  question: "How do we get started working together on my Chelmsford business?",
  answer: "Simply book a free 30-minute SEO consultation using the button below. We will discuss your current local visibility, analyze your competitors, and map out a clear, step-by-step strategy for dominating Chelmsford search results."
}];
const highlights = ["Direct strategy & execution by Yassine Essaadi", "Tailored specifically for Chelmsford & Essex markets", "No hidden fees or long-term contract lock-ins", "Focus on map pack positions & high-converting leads"];
export default function ChelmsfordFaq() {
  return <section id="chelmsfordlocalseofaq" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-96 h-96 bg-secondary/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <Badge variant="outline" className="px-3 py-1 border-primary/20 bg-primary/5 text-primary gap-1.5 font-medium">
            <HelpCircle className="size-3.5 text-primary" />
            Chelmsford Local SEO FAQ
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Common Questions About Local SEO in Chelmsford
          </h2>
          
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed pt-2">
            Get clear, straightforward answers about growing your organic visibility across Essex. 
            No agency fluff—just honest advice directly from your independent consultant.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mt-12 md:mt-16">
          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-6">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Sparkles className="size-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Have a Specific Question?</h3>
                  <p className="text-xs text-muted-foreground">Work directly with Yassine Essaadi</p>
                </div>
              </div>

              <Separator className="bg-border" />

              <p className="text-sm text-foreground/80 leading-relaxed">
                Local SEO can feel complex, but your strategy shouldn't be. When you get in touch, you will speak straight to me—never a sales representative.
              </p>

              <div className="space-y-3 pt-1">
                {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>)}
              </div>

              <Separator className="bg-border" />

              <div className="space-y-3 pt-2">
                <Button asChild size="lg" className="w-full justify-between gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                  <Link to="/ContactPage">
                    <span>Book Free SEO Call</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full justify-center gap-2 border-border hover:bg-accent">
                  <Link to="/CaseStudiesPage">
                    <MessageSquare className="size-4 text-primary" />
                    <span>Explore My Case Studies</span>
                  </Link>
                </Button>
              </div>

              <div className="text-center pt-2">
                <a href="tel:+447490984430" className="text-xs text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5">
                  <PhoneCall className="size-3" />
                  <span>Direct UK line: +44 7490984430</span>
                </a>
              </div>
            </div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-0" className="w-full space-y-3">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border border-border bg-card rounded-lg px-5 sm:px-6 shadow-xs transition-colors hover:border-primary/40 data-[state=open]:border-primary/40 data-[state=open]:shadow-sm">
                  <AccordionTrigger className="text-primary font-semibold text-base md:text-lg hover:no-underline py-5 text-left leading-snug">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base pt-1 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}